// Shared attendance helpers (pure, wall-clock based — no timezone conversion).

export const WORK_END_HOUR = 17;
export const WORK_END_MINUTE = 0;
export const EARLY_DEPARTURE_GRACE_MINUTES = 0;
export const WORK_START_MIN = 9 * 60;       // 09:00
export const EARLY_ARRIVAL_MIN = 8 * 60 + 30; // 08:30
export const WORK_END_MIN = 17 * 60;        // 17:00

// Reasons HR can give when marking an employee Present on their behalf.
// Attendance is record-keeping only — it never drives payroll deductions.
export const PRESENCE_REASONS = [
  { value: 'client_meeting', label: 'Client Meeting' },
  { value: 'outstation', label: 'Outstation / Site Visit' },
  { value: 'work_from_home', label: 'Work From Home' },
  { value: 'training', label: 'Training / Seminar' },
  { value: 'forgot_clock_in', label: 'Forgot to Clock In' },
  { value: 'other', label: 'Other' },
];
export const presenceReasonLabel = (value) => PRESENCE_REASONS.find((r) => r.value === value)?.label || '';
// Statuses that mean the employee attended (HR may record these on the employee's behalf).
export const ATTENDED_STATUSES = ['present', 'late', 'half_day', 'early', 'on_time'];
export const isAttendedStatus = (status) => ATTENDED_STATUSES.includes((status || '').toLowerCase());
// HR-marked rows: new rows carry `marked_by_hr`; legacy manual entries only have `created_by`.
export const isHrMarked = (record) => Boolean(record?.marked_by_hr || record?.presence_reason || record?.created_by);
export const showsHrMarkedPill = (record) => isHrMarked(record) && isAttendedStatus(record?.status);
export const countsAsPresent = (record) => Boolean(record?.clock_in || record?.presence_reason);

// HR types attendance as wall-clock times; the backend stores ISO with a `+00:00`
// suffix purely for shape. Extract HH:MM directly — never re-convert to browser tz.
export const formatAttendanceTime = (raw, opts = {}) => {
  const { withMeridiem = false } = opts;
  if (!raw) return '-';
  const s = String(raw).trim();
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) m = s.match(/T(\d{2}):(\d{2})/);
  if (!m) return '-';
  const hh24 = parseInt(m[1], 10);
  const mm = m[2];
  if (Number.isNaN(hh24) || hh24 < 0 || hh24 > 23) return '-';
  if (!withMeridiem) return `${String(hh24).padStart(2, '0')}:${mm}`;
  const period = hh24 >= 12 ? 'PM' : 'AM';
  const hh12 = ((hh24 + 11) % 12) + 1;
  return `${String(hh12).padStart(2, '0')}:${mm} ${period}`;
};

// Minutes-since-midnight from HH:MM / HH:MM:SS / ISO, without tz conversion.
export const attendanceMinutes = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) m = s.match(/T(\d{2}):(\d{2})/);
  if (!m) return null;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

// Wall-clock minutes between clock_in and clock_out (overnight → next day).
export const workedDiffMinutes = (record) => {
  if (!record?.clock_in || !record?.clock_out) return null;
  const ciIso = new Date(record.clock_in);
  const coIso = new Date(record.clock_out);
  if (!Number.isNaN(ciIso.getTime()) && !Number.isNaN(coIso.getTime())) {
    let diffMs = coIso.getTime() - ciIso.getTime();
    if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
    return Math.floor(diffMs / 60000);
  }
  const ci = attendanceMinutes(record.clock_in);
  const co = attendanceMinutes(record.clock_out);
  if (ci === null || co === null) return null;
  let mins = co - ci;
  if (mins < 0) mins += 24 * 60;
  return mins;
};

export const formatWorkedHours = (record) => {
  if (!record?.clock_in) return '-';
  if (!record?.clock_out) return 'In progress';
  const mins = workedDiffMinutes(record);
  if (mins === null) return '-';
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export const workedMinutes = (record) => {
  const m = workedDiffMinutes(record);
  return m === null ? 0 : m;
};

export const formatMinutes = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

export const isEarlyDeparture = (record) => {
  if (!record?.clock_in || !record?.clock_out) return false;
  const out = attendanceMinutes(record.clock_out);
  if (out === null) return false;
  return out < (WORK_END_MIN - EARLY_DEPARTURE_GRACE_MINUTES);
};

// Approved leave covering the given date for the employee (null if none).
export const isOnLeaveOnDate = (employeeId, dateStr, approvedLeaves) => {
  if (!employeeId || !dateStr) return null;
  return (approvedLeaves || []).find((l) => {
    if (l.employee_id !== employeeId) return false;
    const s = (l.start_date || '').slice(0, 10);
    const e = (l.end_date || s).slice(0, 10);
    if (dateStr < s || dateStr > e) return false;
    return !(l.excluded_dates || []).some((x) => (typeof x === 'string' ? x : x?.date) === dateStr);
  }) || null;
};

export const findPublicHoliday = (dateStr, publicHolidays) => {
  if (!dateStr) return null;
  return (publicHolidays || []).find((h) => (h.date || '').slice(0, 10) === dateStr) || null;
};

// Status of a single attendance record. Persisted manual edits win; then HR
// "marked present"; then explicit statuses; then wall-clock derivation.
export const deriveAttendanceStatus = (record) => {
  const persisted = (record?.status || '').toLowerCase();
  if (Boolean(record?.updated_by) && persisted) return persisted;
  if (persisted === 'present' && isHrMarked(record)) return 'present';
  if (['absent', 'half_day', 'on_leave', 'late', 'early', 'on_time'].includes(persisted)) return persisted;
  if (!record?.clock_in) return 'missing_clock_in';
  const ci = attendanceMinutes(record.clock_in);
  if (ci === null) return 'on_time';
  if (ci <= EARLY_ARRIVAL_MIN) return 'early';
  if (ci > WORK_START_MIN) return 'late';
  return 'on_time';
};

export const STATUS_LABELS = {
  early: 'Early',
  on_time: 'On Time',
  late: 'Late',
  absent: 'Absent',
  missing_clock_in: 'Missing Clock-In',
  present: 'Present',
  half_day: 'Half Day',
  on_leave: 'On Leave',
  public_holiday: 'Public Holiday',
  rest_day: 'Rest Day',
  weekend: 'Weekend',
  no_record: 'No Record',
  upcoming: 'Upcoming',
};

const leaveLabel = (leave) => leave?.leave_type_name || leave?.leave_type || leave?.leave_type_id || 'Leave';

// One entry per calendar day of `month` (YYYY-MM) for a single employee.
export const buildMonthlyDays = ({ employeeId, month, records, approvedLeaves, publicHolidays, today }) => {
  const [y, m] = month.split('-').map((n) => parseInt(n, 10));
  const daysInMonth = new Date(y, m, 0).getDate();
  const todayStr = today || new Date().toISOString().slice(0, 10);
  const byDate = new Map();
  (records || []).forEach((r) => {
    const d = (r.date || r.clock_in || '').slice(0, 10);
    if (d && !byDate.has(d)) byDate.set(d, r);
  });
  const days = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = `${month}-${String(day).padStart(2, '0')}`;
    const dow = new Date(`${date}T00:00:00`).getDay();
    const record = byDate.get(date) || null;
    const holiday = findPublicHoliday(date, publicHolidays);
    const isFuture = date > todayStr;
    const isWorkingDay = dow !== 0 && dow !== 6 && !holiday;
    // Leave only "consumes" working days (Leave module excludes weekends + public holidays).
    const leave = isWorkingDay ? isOnLeaveOnDate(employeeId, date, approvedLeaves) : null;
    let status;
    if (record) status = deriveAttendanceStatus(record);
    else if (holiday) status = 'public_holiday';
    else if (dow === 0) status = 'rest_day';
    else if (dow === 6) status = 'weekend';
    else if (leave) status = 'on_leave';
    else if (isFuture) status = 'upcoming';
    else status = 'no_record';
    days.push({
      date,
      day,
      dow,
      record,
      status,
      holiday,
      leave,
      leaveLabel: leave ? `${leaveLabel(leave)}${leave.is_half_day ? ' (half day)' : ''}` : '',
      isFuture,
      isWorkingDay,
      earlyDeparture: record ? isEarlyDeparture(record) : false,
      hrMarked: record ? showsHrMarkedPill(record) : false,
      presenceReason: record ? presenceReasonLabel(record.presence_reason) : '',
      hours: record ? formatWorkedHours(record) : '-',
      minutes: record ? workedMinutes(record) : 0,
    });
  }
  return days;
};

export const summarizeMonthlyDays = (days) => {
  const s = {
    workingDays: 0, daysPresent: 0, late: 0, earlyDepartures: 0, onLeave: 0,
    publicHolidays: 0, noRecord: 0, hrMarked: 0, totalMinutes: 0,
  };
  days.forEach((d) => {
    if (d.isWorkingDay) s.workingDays += 1;
    if (d.holiday) s.publicHolidays += 1;
    const presentStatuses = ['present', 'late', 'early', 'on_time', 'half_day'];
    if (d.record && presentStatuses.includes(d.status)) s.daysPresent += 1;
    if (d.status === 'late') s.late += 1;
    if (d.earlyDeparture) s.earlyDepartures += 1;
    if (d.status === 'on_leave' || (d.leave && !d.record)) s.onLeave += d.leave?.is_half_day ? 0.5 : 1;
    if (d.status === 'no_record' || d.status === 'missing_clock_in' || d.status === 'absent') s.noRecord += 1;
    if (d.hrMarked) s.hrMarked += 1;
    s.totalMinutes += d.minutes;
  });
  s.totalHours = formatMinutes(s.totalMinutes);
  return s;
};
